'use client';

import { AiToolkitVideoPlayer } from '@app/(darkHeader)/ai/_shared/_components/AiToolkitVideoPlayer/AiToolkitVideoPlayer';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import {
    failureReasonContent,
    IMAGE_SEARCH_MEDIA_TYPE_OPTIONS,
    VIDEO_GENERATION_MODEL_OPTIONS,
} from '@app/(darkHeader)/ai/_shared/_context/constants';
import { canRetry } from '@app/(darkHeader)/ai/_shared/_context/failureReason';
import { AI_TOOLKIT_CATEGORIES, ImageSearchMediaTypeOption } from '@app/(darkHeader)/ai/_shared/_context/types';
import { AiToolkitMainPanel } from '@app/(darkHeader)/ai/_shared/_layouts/AiToolkitMainPanel';
import { GenerationFlowErrorLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowErrorLayout';
import { GenerationFlowIdleView } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowIdleView';
import { GenerationFlowInProgressLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowInProgressLayout';
import { GenerationFlowSuccessLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowSuccessLayout';
import { useObjectUrl } from '@app/_shared/hooks/useObjectUrl';
import sendAiContentDownloadEvent from 'components/components/events/AiContentDownloadEvent';
import { JSX, useCallback, useState } from 'react';

import { Button, cn, ProminentSearch } from '@videoblocks/design-system';
import { GenerateVideo } from '@videoblocks/design-system/icons';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { PostGenerationActionSection } from '../../../_shared/_layouts/PostGenerationActionSection';
import { AnimatedCardDisplay } from '../AnimatedCardDisplay/AnimatedCardDisplay';
import { useVideoGenerationForm } from '../useVideoGenerationForm';
import { ImageSearchLayout } from './Search/ImageSearchLayout';

export const VideoGenerationMainPanel = (): JSX.Element => {
    const { submitStates, formStates, imageSearch } = useAiToolkitContext();
    const {
        status,
        videoUrl,
        sourceThumbnailUrl: restoredThumbnailUrl,
        aiContentId,
        lastAiContentId,
        aiContentType: submittedAiContentType,
        aiModelName: submittedAiModelName,
        failureReason,
    } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION];
    const { searchTerm, setSearchTerm, mediaType, setMediaType, search, setShowSearch, showSearch } = imageSearch;
    const [showSearchClose, setShowSearchClose] = useState(searchTerm.length > 0);
    const file = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]?.file ?? null;
    const selectedLibraryImage = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]?.selectedLibraryImage ?? null;
    const { onConfirm, isValid } = useVideoGenerationForm();
    const fileObjectUrl = useObjectUrl(file);
    const thumbnailUrl = fileObjectUrl || selectedLibraryImage?.thumbnailUrl || restoredThumbnailUrl || undefined;
    const thumbnailAlt = file
        ? `Preview for ${file.name} video is loading...`
        : selectedLibraryImage
          ? `Preview for ${selectedLibraryImage.title} video is loading...`
          : '';

    const isLoading = status === 'inProgress';
    const isSuccess = status === 'success';
    const isError = status === 'error';

    const usesStartFrame = !!file || !!selectedLibraryImage || !!restoredThumbnailUrl;

    // Events report the type/model captured at submit time; fall back to the live-form derivation
    // for generations restored from history, where submit state is null.
    const eventAiContentType =
        submittedAiContentType ?? (usesStartFrame ? AiContentType.IMAGE_TO_VIDEO : AiContentType.TEXT_TO_VIDEO);
    const eventAiModelName = submittedAiModelName ?? VIDEO_GENERATION_MODEL_OPTIONS[0].value;

    const isLoadingWithStartFrame = isLoading && usesStartFrame;

    const handleDownload = useCallback(() => {
        if (!videoUrl) return;
        if (typeof window !== 'undefined') {
            void sendAiContentDownloadEvent({
                aiContentId,
                voiceId: null,
                aiContentVoiceoverType: null,
                currentUrl: window.location.href,
                referrer: document.referrer || undefined,
                lastAiContentId,
                aiModelName: eventAiModelName,
                aiContentType: eventAiContentType,
            });
        }
    }, [videoUrl, aiContentId, lastAiContentId, eventAiContentType, eventAiModelName]);

    const errorContent = failureReason ? failureReasonContent(failureReason) : undefined;
    const errorView = (
        <GenerationFlowErrorLayout
            title={errorContent?.title}
            subtitle={errorContent?.subtitle}
            canRetry={canRetry(failureReason)}
            onConfirm={() => void onConfirm()}
            isValid={isValid}
        />
    );

    const inProgressWithStartFrameView = (
        <GenerationFlowInProgressLayout className={'h-auto gap-6 justify-start'}>
            <AiToolkitVideoPlayer
                state="loading"
                thumbnailUrl={thumbnailUrl}
                thumbnailAlt={thumbnailAlt}
                src={videoUrl ?? ''}
            />
        </GenerationFlowInProgressLayout>
    );

    const inProgressWithoutStartFrameView = <GenerationFlowInProgressLayout className="h-full gap-5 justify-center" />;

    const successView = (
        <GenerationFlowSuccessLayout>
            <AiToolkitVideoPlayer
                state="playback"
                thumbnailUrl={thumbnailUrl}
                thumbnailAlt={thumbnailAlt}
                src={videoUrl ?? ''}
            />
            {isSuccess && (
                <PostGenerationActionSection
                    downloadUrl={videoUrl}
                    downloadFilename="generated-video.mp4"
                    onDownload={handleDownload}
                    category={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
                    aiContentId={aiContentId}
                    lastAiContentId={lastAiContentId}
                    aiContentType={eventAiContentType}
                    aiModelName={eventAiModelName}
                />
            )}
        </GenerationFlowSuccessLayout>
    );

    return (
        <AiToolkitMainPanel
            heading={showSearch ? 'Choose Start Frame' : 'Video Generator'}
            icon={<GenerateVideo width={16} height={16} />}
            className={cn(showSearch && 'max-h-[55vh] md:max-h-[calc(100vh-132px)]')}
            innerContainerClassName={cn(showSearch && 'w-full 2xl:max-w-auto 2xl:p-4')}
        >
            <div className={cn('flex flex-col gap-5 h-full', showSearch && 'gap-4')}>
                <div className="flex gap-2 w-full">
                    <ProminentSearch
                        selectOptions={IMAGE_SEARCH_MEDIA_TYPE_OPTIONS}
                        onSelectChange={(value) => {
                            setMediaType(value as ImageSearchMediaTypeOption);
                        }}
                        defaultSelectValue={mediaType}
                        value={searchTerm}
                        onChange={(e) => {
                            setShowSearch(true);
                            setSearchTerm(e.target.value);
                            setShowSearchClose(e.target.value.trim().length > 0);
                        }}
                        placeholder="Search image library for a start frame..."
                        onSubmit={() => {
                            void search();
                            setShowSearch(true);
                        }}
                        showClose={showSearchClose}
                        onClose={() => {
                            setSearchTerm('');
                            setShowSearchClose(false);
                        }}
                    />
                </div>
                {showSearch ? (
                    <ImageSearchLayout />
                ) : isError ? (
                    errorView
                ) : isLoadingWithStartFrame ? (
                    inProgressWithStartFrameView
                ) : isLoading ? (
                    inProgressWithoutStartFrameView
                ) : isSuccess ? (
                    successView
                ) : (
                    <GenerationFlowIdleView
                        visual={<AnimatedCardDisplay />}
                        title="Create videos from text prompts or images"
                        subtitle="Describe your scene with a simple prompt or add image references."
                    >
                        <Button
                            variant="secondary"
                            className="w-fit"
                            onClick={() => {
                                setShowSearch(true);
                                void search();
                            }}
                        >
                            Browse Images
                        </Button>
                    </GenerationFlowIdleView>
                )}
            </div>
        </AiToolkitMainPanel>
    );
};
